/* eslint-disable react/no-children-prop */
import React, { useState } from 'react';
import { Box, Button, Dialog, makeStyles } from '@material-ui/core';
import { InventoryOrder } from 'src/interfaces/IInventoryOrder';
import { useShipOrder } from 'src/hooks/queries/useInventoryOrders';
import classNames from 'classnames';
import { OrderAddChargesModal } from './AddChargesModal';

type Props = {
  inventoryOrder: InventoryOrder;
  approve: () => void;
  handleAdminNote: () => void;
  handleRejectOrder: () => void;
  isLegacy?: boolean;
};

export const ButtonsFooter = ({ inventoryOrder, handleAdminNote, approve, handleRejectOrder, isLegacy }: Props) => {
  const classes = useStyles();
  const [addChargesDialog, setAddChargesDialog] = useState<boolean>(false);
  const [addDiscountsDialog, setAddDiscountsDialog] = useState<boolean>(false);
  const [shipping, setShipping] = useState<boolean>(false);

  const { mutateAsync: shipOrder } = useShipOrder(inventoryOrder.id);

  const handleShip = async () => {
    setShipping(true);
    await shipOrder();
    setShipping(false);
  };

  return (
    <>
      <Box paddingY={0} className={classes.footerButtons}>
        <Button
          onClick={() => setAddChargesDialog(true)}
          variant="outlined"
          className={classNames(classes.actionButton, classes.chargesButton)}
          disabled={!inventoryOrder.cancelable}
        >
          Add Additional Charge
        </Button>
        <Button
          onClick={() => setAddDiscountsDialog(true)}
          variant="outlined"
          className={classNames(classes.actionButton, classes.chargesButton)}
          disabled={!inventoryOrder.cancelable}
        >
          Add Discount
        </Button>
        <Button
          onClick={handleAdminNote}
          variant="outlined"
          className={classNames(classes.actionButton, classes.discountsButton)}
          disabled={!inventoryOrder.cancelable}
        >
          Add Note
        </Button>
        <Box className={classes.buttonsContainer}>
          <Button
            data-cy="view"
            onClick={handleRejectOrder}
            variant="outlined"
            className={classNames(classes.actionButton, classes.rejectButton)}
            data-testid={`order reject ${inventoryOrder.id}`}
            disabled={!inventoryOrder.cancelable}
          >
            Reject
          </Button>
          {isLegacy && inventoryOrder.approved ? (
            <Button
              data-cy="view"
              onClick={handleShip}
              variant="contained"
              className={classes.actionButton}
              data-testid={`order ship ${inventoryOrder.id}`}
              disabled={shipping || !inventoryOrder.placed || inventoryOrder?.purchaseOrderTotal !== 0}
            >
              Ship Order
            </Button>
          ) : (
            <Button
              data-cy="view"
              onClick={approve}
              variant="contained"
              className={classes.actionButton}
              data-testid={`order approve ${inventoryOrder.id}`}
              disabled={!inventoryOrder.cancelable || !inventoryOrder.allSuppliersApproved}
            >
              Approve
            </Button>
          )}
        </Box>
      </Box>
      <Dialog
        open={addChargesDialog}
        maxWidth="lg"
        children={<OrderAddChargesModal id={inventoryOrder.id} dismiss={() => setAddChargesDialog(false)} />}
      />
      <Dialog
        open={addDiscountsDialog}
        maxWidth="lg"
        children={
          <OrderAddChargesModal id={inventoryOrder.id} dismiss={() => setAddDiscountsDialog(false)} discountMode />
        }
      />
    </>
  );
};

const useStyles = makeStyles(() => ({
  footerButtons: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginTop: 15,
  },
  buttonsContainer: {
    display: 'flex',
    gap: '1rem',
  },
  actionButton: {
    textTransform: 'none',
    fontSize: 14,
    minWidth: 132,
    height: 'auto',
    borderColor: '#ccc',
    color: '#000',
    backgroundColor: 'rgba(51,126,137,0)',
    '&:first-child': {
      margin: 'auto auto auto 0',
      padding: '8px 18px',
    },
    '&:last-child': {
      backgroundColor: 'rgba(29, 88, 77, 1)', // #1D584D
      border: '2px solid #1D584D',
      color: '#fff',
      margin: 'auto 0',
      padding: '8px 18px',
      '&:disabled': {
        backgroundColor: 'rgba(29,88,77,0.65)',
        border: 'none',
      },
    },
    '&:last-child:hover': {
      backgroundColor: 'rgba(29, 88, 77, 0.45)',
    },
  },
  rejectButton: {
    color: '#E63E3E',
    border: '2px solid #E63E3E',

    '&:hover': {
      color: '#fff',
      backgroundColor: '#E63E3E',
    },
  },
  chargesButton: {
    padding: '8px 18px',
    margin: 'auto 15px auto 0 !important',
  },
  discountsButton: {
    padding: '8px 18px',
    margin: 'auto auto auto 0',
  },
}));
